import './announcement_region.js';

/**
 * AnnouncementService - Centralized service for screen reader announcements
 * Provides a singleton service to announce status messages to screen readers
 * without any visual display
 */
export default class AnnouncementService {
  constructor() {
    this.region = null;
    this.initialized = false;
  }

  /**
   * Initializes the announcement service
   * Creates and adds the global announcement region to the DOM
   * This should be called from theme.js or app initialization
   */
  init() {
    if (this.initialized || this.region) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._createRegion());
    } else {
      this._createRegion();
    }
  }

  /**
   * Creates the announcement region element
   * @private
   */
  _createRegion() {
    this.region = document.createElement('announcement-region');
    this.region.id = 'global-announcements';

    // Add to DOM immediately - completely invisible but accessible
    document.body.appendChild(this.region);
    this.initialized = true;
  }

  /**
   * Announces a message to screen readers
   * @param {string} message - The message to announce
   * @param {string} type - The type of announcement (error, success, warning, info, status)
   * @param {Object} options - Additional options (duration, priority)
   */
  announce(message, type = 'status', options = {}) {
    if (!this.initialized || !this.region) {
      console.warn('AnnouncementService not initialized. Call announcements.init() from theme.js');
      return;
    }

    this.region.announce(message, type, options);
  }

  /**
   * Announces an error message with assertive priority
   * @param {string} message - The error message
   * @param {Object} options - Additional options
   */
  error(message, options = {}) {
    this.announce(message, 'error', { ...options, priority: 'assertive' });
  }

  /**
   * Announces a success message
   * @param {string} message - The success message
   * @param {Object} options - Additional options
   */
  success(message, options = {}) {
    this.announce(message, 'success', options);
  }

  /**
   * Announces a warning message with assertive priority
   * @param {string} message - The warning message
   * @param {Object} options - Additional options
   */
  warning(message, options = {}) {
    this.announce(message, 'warning', { ...options, priority: 'assertive' });
  }

  /**
   * Announces an informational message
   * @param {string} message - The info message
   * @param {Object} options - Additional options
   */
  info(message, options = {}) {
    this.announce(message, 'info', options);
  }

  /**
   * Clears the current announcement
   */
  clear() {
    if (this.region) {
      this.region.clear();
    }
  }
}


